/**
 * Hours: planned by heads, logged by people, measured against capacity.
 *
 * Fed by `GET /tech-workload/overview` (node-portal src/techWorkload/), which sends the people, their tasks,
 * plans, entries and calendars.
 *
 * ── How a plan becomes hours on a day ───────────────────────────────────────
 *
 *   Past days     what was actually logged that day.
 *   Today         what was logged today, plus today's share of what is left.
 *   Future days   what is left — planned minus everything logged so far —
 *                 spread evenly over the working days up to the due date.
 *
 * Remaining work therefore always lands on the days still to come, whatever
 * happened before: a hold, a pushed deadline, a slow week. Nothing needs to
 * remember which days a task sat on hold — the logged time already says what
 * really happened, and the due date the hold extended says how long is left.
 *
 *   On hold       remaining hours are paused: 0 a day, reported separately.
 *   In review,
 *   Approved,
 *   Completed     handed over, so nothing more is expected of the assignee.
 *   Overdue       remaining hours all land on today.
 *   No due date   cannot be spread; reported as unscheduled.
 *
 * Week-offs and department holidays have no capacity and receive no share.
 */

import { HANDED_OVER, LOAD_ORDER, STATUS } from './constants';
import { addDays, daysBetween, startOfMonth } from './dates';
import { isHandedOver, shownInRange } from './tasks';

/* ── Levels ──────────────────────────────────────────────────────────────── */

export function levelForPct(pct) {
  if (pct > 100) return 'overloaded';
  if (pct >= 85) return 'atCapacity';
  if (pct >= 50) return 'balanced';
  return 'available';
}

/* ── Formatting ─────────────────────────────────────────────────────────── */

export const round1 = (value) => Math.round((Number(value) || 0) * 10) / 10;

/** 6 → "6.0h", 2.25 → "2.3h". */
export const fmtHours = (value) => `${round1(value).toFixed(1)}h`;

/** Minutes between two `HH:MM` strings. */
export function minutesBetween(from, to) {
  const toMin = (text) => {
    const [h, m] = String(text || '').split(':').map(Number);
    return h * 60 + m;
  };
  return toMin(to) - toMin(from);
}

/* ── Index ──────────────────────────────────────────────────────────────── */

const DEFAULT_CALENDAR = { weekOff: [0], dates: [] };

/**
 * Lookups over one `/tech-workload/overview` payload, plus a cache of each plan's
 * spread so a month of calendar cells does not recompute it per cell.
 */
export function indexHours(data, today) {
  const plans = (data?.plans || []).map((plan) => ({ ...plan, logged: (plan.loggedMinutes || 0) / 60 }));
  const byKey = new Map(plans.map((plan) => [`${plan.taskId}:${plan.personId}`, plan]));
  const byPerson = new Map();
  plans.forEach((plan) => {
    if (!byPerson.has(plan.personId)) byPerson.set(plan.personId, []);
    byPerson.get(plan.personId).push(plan);
  });

  const entries = data?.entries || [];
  const loggedOn = new Map();
  const entriesByPlan = new Map();
  entries.forEach((entry) => {
    const key = `${entry.hourId}|${entry.workDate}`;
    loggedOn.set(key, (loggedOn.get(key) || 0) + entry.minutes / 60);
    if (!entriesByPlan.has(entry.hourId)) entriesByPlan.set(entry.hourId, []);
    entriesByPlan.get(entry.hourId).push(entry);
  });

  const calendars = data?.calendars || {};
  const offDates = new Map(
    Object.entries(calendars).map(([id, calendar]) => [Number(id), new Set((calendar.dates || []).map((d) => d.date))]),
  );

  return {
    today,
    capacityPerDay: Number(data?.capacityPerDay) || 8,
    canPlan: new Set((data?.canPlan || []).map(Number)),
    plans,
    byKey,
    byPerson,
    entries,
    entriesByPlan,
    loggedOn,
    calendarOf: (personId) => calendars[personId] || DEFAULT_CALENDAR,
    offDatesOf: (personId) => offDates.get(Number(personId)) || new Set(),
    shares: new Map(),
  };
}

export function isWorkingDay(idx, personId, day) {
  const weekday = new Date(`${day}T00:00:00`).getDay();
  if ((idx.calendarOf(personId).weekOff || []).includes(weekday)) return false;
  return !idx.offDatesOf(personId).has(day);
}

/** Planned minus everything logged, never below zero. */
export const remainingOf = (plan) => Math.max(0, plan.plannedHours - plan.logged);

export const isPaused = (plan) => plan.task.status === STATUS.ON_HOLD;

/**
 * Where a plan's remaining hours fall from today on: `Map(day → hours)`.
 * Empty when there is nothing left, it is paused, handed over, or undated.
 */
function futureShare(plan, idx) {
  if (idx.shares.has(plan.hourId)) return idx.shares.get(plan.hourId);

  const share = new Map();
  const left = remainingOf(plan);
  const { status, dueDate, startDate, createdOn } = plan.task;

  if (left > 0 && dueDate && status !== STATUS.ON_HOLD && !HANDED_OVER.includes(status)) {
    if (dueDate < idx.today) {
      share.set(idx.today, left);
    } else {
      const begin = [startDate || createdOn || idx.today, idx.today].sort().pop();
      const days = daysBetween(begin, dueDate, 366).filter((day) => isWorkingDay(idx, plan.personId, day));
      if (!days.length) share.set(begin, left);
      else days.forEach((day) => share.set(day, left / days.length));
    }
  }

  idx.shares.set(plan.hourId, share);
  return share;
}

/** Hours one plan puts on one day. */
export function planHoursOn(plan, day, idx) {
  const logged = idx.loggedOn.get(`${plan.hourId}|${day}`) || 0;
  if (day < idx.today) return logged;
  return (day === idx.today ? logged : 0) + (futureShare(plan, idx).get(day) || 0);
}

export function capacityOn(idx, personId, day) {
  return isWorkingDay(idx, personId, day) ? idx.capacityPerDay : 0;
}

/** Hours from a person's plans on one day, optionally one project only. */
export function personHoursOn(idx, personId, day, projectId = null) {
  return (idx.byPerson.get(personId) || [])
    .filter((plan) => !projectId || plan.task.projectId === projectId)
    .reduce((sum, plan) => sum + planHoursOn(plan, day, idx), 0);
}

const pctOf = (allocated, capacity) => (capacity > 0 ? (allocated / capacity) * 100 : allocated > 0 ? 999 : 0);

/* ── Range summary ──────────────────────────────────────────────────────── */

/**
 * One row per person over a date range, and team totals.
 *
 * `items` joins the tasks in range with their plans: open ones (every stage up
 * to Review pending / Approved) by their dates, and Completed ones on every day
 * from their start up to the day they were completed (see utils/tasks `workedInRange`) — so
 * finished work shows with its status even when nobody set hours for it. It
 * also adds any planned task with time logged in the range that the list does
 * not carry.
 *
 * Only work still with the person counts as "needs hours" or in `tasks`: a task
 * in review, approved or completed has been handed over, and nobody will plan
 * hours for it now.
 */
export function summariseHours({ people, tasks, idx, from, to, projectId = null }) {
  const days = daysBetween(from, to);
  const today = idx.today;

  const rows = people.map((person) => {
    const mine = (idx.byPerson.get(person.id) || []).filter(
      (plan) => !projectId || plan.task.projectId === projectId,
    );

    let allocated = 0;
    let capacity = 0;
    let logged = 0;
    days.forEach((day) => {
      capacity += capacityOn(idx, person.id, day);
      mine.forEach((plan) => {
        allocated += planHoursOn(plan, day, idx);
        logged += idx.loggedOn.get(`${plan.hourId}|${day}`) || 0;
      });
    });

    // Tasks held in the range, with their plans: open ones by their dates, completed ones from their start up to the day they were completed.
    const held = tasks.filter(
      (task) =>
        task.assigneeIds.includes(person.id) &&
        (!projectId || task.projectId === projectId) &&
        shownInRange(task, from, to, today),
    );
    const items = held.map((task) => ({ task, plan: idx.byKey.get(`${task.taskId}:${person.id}`) || null }));
    const listed = new Set(items.map((item) => item.task.taskId));

    // Planned tasks the open list does not carry, with time logged in range.
    mine
      .filter((plan) => !listed.has(plan.taskId))
      .filter((plan) => days.some((day) => idx.loggedOn.has(`${plan.hourId}|${day}`)))
      .forEach((plan) => items.push({ task: { ...plan.task, assigneeIds: [person.id] }, plan }));

    // Still with the person: not in review, approved or completed.
    const dated = items.filter((item) => item.task.dueDate && !isHandedOver(item.task));
    const needsHours = dated.filter((item) => !item.plan).length;
    const paused = mine.filter(isPaused).reduce((sum, plan) => sum + remainingOf(plan), 0);
    const unscheduled = mine
      .filter((plan) => !plan.task.dueDate && !isHandedOver(plan.task) && !isPaused(plan))
      .reduce((sum, plan) => sum + remainingOf(plan), 0);

    const pct = pctOf(allocated, capacity);

    return {
      person,
      items,
      tasks: dated.length,
      allocated,
      capacity,
      pct,
      logged,
      needsHours,
      paused,
      unscheduled,
      level: levelForPct(pct),
      canPlan: idx.canPlan.has(person.id),
    };
  });

  rows.sort((a, b) => b.pct - a.pct || String(a.person.name).localeCompare(String(b.person.name)));

  const levels = Object.fromEntries(LOAD_ORDER.map((key) => [key, 0]));
  rows.forEach((row) => {
    levels[row.level] += 1;
  });

  const sumOf = (list, pick) => list.reduce((sum, row) => sum + pick(row), 0);
  const of = (key) => rows.filter((row) => row.level === key);

  const allocated = sumOf(rows, (row) => row.allocated);
  const capacity = sumOf(rows, (row) => row.capacity);

  return {
    rows,
    levels,
    totals: {
      people: rows.length,
      allocated,
      capacity,
      utilisation: capacity ? (allocated / capacity) * 100 : 0,
      logged: sumOf(rows, (row) => row.logged),
      over: sumOf(of('overloaded'), (row) => row.allocated - row.capacity),
      headroomAtCapacity: sumOf(of('atCapacity'), (row) => Math.max(0, row.capacity - row.allocated)),
      headroomBalanced: sumOf(of('balanced'), (row) => Math.max(0, row.capacity - row.allocated)),
      freeAvailable: sumOf(of('available'), (row) => Math.max(0, row.capacity - row.allocated)),
      needsHours: sumOf(rows, (row) => row.needsHours),
      paused: sumOf(rows, (row) => row.paused),
    },
  };
}

/* ── Calendar ───────────────────────────────────────────────────────────── */

/** Team hours, capacity and level counts for each day. */
export function teamDays({ people, idx, days, projectId = null }) {
  return days.map((day) => {
    let allocated = 0;
    let capacity = 0;
    const levels = Object.fromEntries(LOAD_ORDER.map((key) => [key, 0]));
    /* Who is at each level, for the calendar's hover lists — busiest first. */
    const names = Object.fromEntries(LOAD_ORDER.map((key) => [key, []]));
    let working = 0;

    people.forEach((person) => {
      const cap = capacityOn(idx, person.id, day);
      const hours = personHoursOn(idx, person.id, day, projectId);
      allocated += hours;
      capacity += cap;
      if (cap > 0) {
        working += 1;
        const pct = pctOf(hours, cap);
        const level = levelForPct(pct);
        levels[level] += 1;
        names[level].push({ person, hours, pct });
      }
    });

    Object.values(names).forEach((list) => list.sort((a, b) => b.pct - a.pct));

    return {
      day,
      allocated,
      capacity,
      pct: capacity ? (allocated / capacity) * 100 : 0,
      levels,
      names,
      workingDay: working > 0,
    };
  });
}

/** One person's hours for each of the given days. */
export function personWeek(idx, personId, days, projectId = null) {
  return days.map((day) => ({
    day,
    hours: personHoursOn(idx, personId, day, projectId),
    capacity: capacityOn(idx, personId, day),
  }));
}

/** The span of days `/tech-workload/overview` must cover for entries: the range, the calendar grid, today. */
export function fetchSpan(range, gridDays, today) {
  const all = [range.from, range.to, today, gridDays[0], gridDays[gridDays.length - 1]].filter(Boolean).sort();
  // A week either side, so the drawer's week chart always has its logged days.
  return { from: addDays(all[0], -7), to: addDays(all[all.length - 1], 7) };
}

/* ── Page figures ───────────────────────────────────────────────────────── */

/** Hours each project puts on the people on the page over the range, as a Map of projectId to hours. */
export function projectHoursIn(idx, from, to) {
  const days = daysBetween(from, to);
  const out = new Map();
  idx.plans.forEach((plan) => {
    const total = days.reduce((sum, day) => sum + planHoursOn(plan, day, idx), 0);
    out.set(plan.task.projectId, (out.get(plan.task.projectId) || 0) + total);
  });
  return out;
}

/** The calendar's headline figures for the anchor's month: average, busiest and lightest working day, days with overload. */
export function monthStats(daily, anchor) {
  const month = startOfMonth(anchor).slice(0, 7);
  const workingDays = daily.filter((cell) => cell.day.startsWith(month) && cell.workingDay);
  return {
    workingDays: workingDays.length,
    average: workingDays.length ? workingDays.reduce((sum, cell) => sum + cell.pct, 0) / workingDays.length : 0,
    busiest: workingDays.reduce((best, cell) => (!best || cell.pct > best.pct ? cell : best), null),
    lightest: workingDays.reduce((best, cell) => (!best || cell.pct < best.pct ? cell : best), null),
    overloadDays: workingDays.filter((cell) => cell.levels.overloaded > 0).length,
  };
}
